import type { ReactNode } from "react"
import Tooltip from "@/v2-ui/tooltip"
import Icon, { type IconProps } from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import Link, {
  type LinkProps,
} from "@/v2-backoffice-client/components/link/Link"
import {
  STACKED_ICON_SIZE,
  DEFAULT_ICON_SIZE,
  getButtonClasses,
  type ButtonSize,
  type ButtonVariant,
} from "@/v2-backoffice-client/components/button/button.constants"

// Omit strips React's global `content?: string` HTML attribute so our
// ReactNode-typed tooltip content doesn't intersect with it (same fix
// as TooltipButton).
export type TooltipLinkProps = Omit<
  LinkProps,
  "variant" | "size" | "to" | "content"
> & {
  to?: LinkProps["to"]
  content?: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  isSquare?: boolean
  isStacked?: boolean
  iconSrc?: IconProps["src"]
  iconSize?: number
  disabled?: boolean
}

function TooltipLink(props: TooltipLinkProps) {
  const {
    content,
    variant = "ghost",
    size = "xs",
    isSquare,
    isStacked,
    iconSrc,
    iconSize,
    disabled,
    className,
    children,
    ...linkProps
  } = props

  // `children` is `undefined` when the caller doesn't pass any (icon-only
  // link). Use a nullish check so both `null` and `undefined` count as
  // "no label".
  const hasChildren = children !== null && children !== undefined
  // Show the tooltip whenever `content` is supplied, label or not, so labelled
  // links (e.g. "Mixpanel", "Entra") still surface their help text on hover.
  const shouldShowTooltip = !!content

  const to = linkProps.to || "#"

  const link = (
    <Link
      variant="unstyled"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      iconSrc={null}
      className={cn(
        // Links styled as buttons share the button class system, so the two
        // stay visually identical (see components/button/button.css).
        getButtonClasses({
          variant,
          size,
          isStacked,
          isIconOnly: isSquare ?? (!hasChildren && !isStacked),
        }),
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      {...linkProps}
      to={to}
    >
      {iconSrc && (
        <Icon
          src={iconSrc}
          size={iconSize || (isStacked ? STACKED_ICON_SIZE : DEFAULT_ICON_SIZE)}
        />
      )}
      {children}
    </Link>
  )

  if (!shouldShowTooltip) return link

  return (
    <Tooltip>
      <Tooltip.Trigger tabIndex={-1}>{link}</Tooltip.Trigger>
      <Tooltip.Content side="top">{content}</Tooltip.Content>
    </Tooltip>
  )
}

export default TooltipLink
